// Enhanced ChatBubble.js with modern read receipts and improved link styling
import React, { useRef, useEffect } from 'react';
import { Box, Typography, Link as MuiLink, Avatar, Chip, Tooltip } from '@mui/material';
import { CheckCircle, CheckCircleOutline, Schedule, Visibility } from '@mui/icons-material';
import BaseBubble from './BaseBubble';
import MessageReactions from './MessageReactions';
import ReplyPreview from './ReplyPreview';
import { formatMessageContent, getFirstReaderDate, isMessageMine } from '../../Utils/constants';
import { toast } from 'sonner';

export default function ChatBubble({
  msg,
  userId,
  isTcc,
  handleDeleteMessage,
  deleteMessageLoading,
  handleReadReceipts,
  participants = [],
  conversationId,
  onReactionChange,
  onReactionAdd,
  onReactionRemove,
  onReply,
  onQuote,
  onReplyClick,
  conversationColor,
  sxMsgText = {}
}) {
  const isUserMessage = isMessageMine(userId, msg);
  const senderId = `${msg.sender_id || msg.creator_id}`;
  const readOnDate = getFirstReaderDate(msg.read_receipts, senderId);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && msg.reply_to) {
      // Keep debug logging in development but don't show toasts
      console.log('ChatBubble - Message with reply data:', {
        messageId: msg.id,
        isReply: msg.is_reply,
        isQuote: msg.is_quote,
        replyTo: msg.reply_to,
        replyToMessage: msg.reply_to_message_id
      });
    }
  }, [msg]);

  let senderName = '';
  if (msg.sender_id) {
    // Prefer first_name + last_name, fall back to display_name for business accounts
    const firstName = msg.sender?.first_name;
    const lastName = msg.sender?.last_name;
    if (firstName || lastName) {
      senderName = `${firstName || ''} ${lastName || ''}`.trim() || msg.sender?.display_name || 'N/A';
    } else {
      senderName = msg.sender?.display_name || 'N/A';
    }
  } else if (msg.creator_id) {
    senderName = msg.sender?.display_name || msg.creator_id || 'N/A';
  } else {
    senderName = 'Blitz System';
  }

  let avatarUrl =
    msg.sender?.pfphref
    || msg.sender?.phphref
    || msg.creator?.pfphref
    || '';

  if (!avatarUrl && participants.length > 0) {
    const participant = participants.find(p => p.id === senderId);
    avatarUrl = participant?.pfp || '';
  }

  const isNumeric = /^\d+$/.test(senderId);
  const profileLink = isNumeric ? `/profile/${senderId}` : `/profile/${senderId}`;
  const bubbleRef = useRef(null);

  // Reaction handler for the options menu - opens reaction picker
  const handleReact = (message, anchorFromMenu) => {
//handle reaction
  };

  const handleReply = () => {
    if (onReply) {
      onReply(msg);
    }
  };

  const handleQuote = () => {
    if (onQuote) {
      onQuote(msg);
    }
  };

  // Enhanced reply data processing
  const getReplyData = () => {
    if (!msg.reply_to) return null;

    return {
      id: msg.reply_to.id,
      message: msg.reply_to.message || msg.reply_to.full_message || '',
      sender: msg.reply_to.sender_name ? {
        first_name: msg.reply_to.sender_name
      } : null,
      creator_id: msg.reply_to.sender_name,
      created_at: msg.reply_to.created_at,
      attachments: msg.reply_to.has_attachments
    };
  };

  // Modern read receipt component with tick states:
  // Single tick (outline) = sending/pending
  // Filled tick (white) = confirmed/delivered
  // Green tick = read
  const ReadReceiptIndicator = () => {
    const getReceiptStatus = () => {
      // Failed message
      if (msg.is_failed) {
        return {
          status: 'failed',
          icon: <CheckCircleOutline sx={{ fontSize: 14 }} />,
          color: '#f44336', // Red for failed
          label: msg.error_message || 'Failed to send'
        };
      }

      // Sending/Pending - single tick (outline, low opacity)
      // Check for client-side optimistic OR server-side pending
      if (msg.sending || msg.isOptimistic || msg.is_pending) {
        return {
          status: 'pending',
          icon: <CheckCircleOutline sx={{ fontSize: 14 }} />,
          color: isUserMessage ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
          label: 'Sending...'
        };
      }

      // Read - green filled tick
      if (readOnDate) {
        return {
          status: 'read',
          icon: <CheckCircle sx={{ fontSize: 14 }} />,
          color: '#4CAF50',
          label: `Read ${readOnDate.toLocaleString(undefined, {
            dateStyle: 'short',
            timeStyle: 'short',
            hour12: true
          })}`
        };
      }

      // Confirmed/Delivered - filled tick (white/dark)
      return {
        status: 'delivered',
        icon: <CheckCircle sx={{ fontSize: 14 }} />,
        color: isUserMessage ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.5)',
        label: 'Delivered'
      };
    };

    const receipt = getReceiptStatus();

    return (
      <Tooltip title={receipt.label} placement="top">
        <Box
          onClick={() => handleReadReceipts(msg)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: receipt.color,
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          {receipt.icon}
        </Box>
      </Tooltip>
    );
  };

  // Enhanced timestamp component - WhatsApp style (time only, no date)
  const MessageTimestamp = () => {
    const messageTime = new Date(msg.created_at);

    const timeDisplay = messageTime.toLocaleTimeString(undefined, {
      timeStyle: 'short',
      hour12: true
    });

    return (
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          color: isUserMessage ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.4)',
          userSelect: 'none'
        }}
      >
        {timeDisplay}
      </Typography>
    );
  };

  const replyData = getReplyData();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
        
        mb: 1,
        maxWidth: '100%',
        position: 'relative',
      }}
    >
      {isUserMessage ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '85%' }}>
          <BaseBubble
            ref={bubbleRef}
            message={msg}
            isUserMessage={isUserMessage}
            isTcc={isTcc}
            handleDeleteMessage={() => handleDeleteMessage(msg.id)}
            deleteMessageLoading={deleteMessageLoading}
            onReply={handleReply}
            onQuote={handleQuote}
            onReact={handleReact}
            onReactionAdd={onReactionAdd}
            onReactionRemove={onReactionRemove}
            conversationColor={conversationColor}
            sx={{ minWidth: '140px', maxWidth: '100%' }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: 'left',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.4,
                fontSize: '16px',
                '& a': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(255, 255, 255, 0.6)',
                  textUnderlineOffset: '2px',
                  transition: 'all 0.2s ease',
                  '&:hover': { color: '#ffffff' },
                  '&:visited': { color: 'rgba(255, 255, 255, 0.8)' }
                },
                '& .mention-tag': { color: '#90caf9', fontWeight: 600 },
                ...sxMsgText
              }}
            >
              {formatMessageContent(msg.message, participants, isUserMessage)} 
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.25 }}>
                <MessageTimestamp />
               <ReadReceiptIndicator />
             
            </Box>
            </Typography>
        
          </BaseBubble>
          {(msg.reaction_counts || msg.total_reactions > 0) && (
            <Box sx={{ mt: 0.25, display: 'flex', justifyContent: 'flex-end' }}>
              <MessageReactions
                message={msg}
                isUserMessage={isUserMessage}
                reactions={msg.reaction_counts || {}}
                totalReactions={msg.total_reactions || 0}
                userReactions={Array.isArray(msg.user_reactions) ? msg.user_reactions : []}
                onReactionAdd={onReactionAdd}
                onReactionRemove={onReactionRemove}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '85%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <Avatar
              src={avatarUrl || '/default-avatar.png'}
              alt={senderName}
              sx={{ width: 40, height: 40, flexShrink: 0, mt: 0.25 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary', mb: 0.5 , width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                <MuiLink href={profileLink} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {senderName}
                </MuiLink>
              </Typography>
              {replyData && (
                <Box sx={{ mb: 0.5, maxWidth: '100%' }}>
                  <ReplyPreview
                    replyToMessage={replyData}
                    isQuote={msg.is_quote}
                    compact={true}
                    onClick={() => onReplyClick && onReplyClick(replyData.id)}
                    clickable={!!onReplyClick}
                  />
                </Box>
              )}
              <BaseBubble
                ref={bubbleRef}
                message={msg}
                isUserMessage={isUserMessage}
                isTcc={isTcc}
                handleDeleteMessage={() => handleDeleteMessage(msg.id)}
                deleteMessageLoading={deleteMessageLoading}
                onReply={handleReply}
                onQuote={handleQuote}
                onReact={handleReact}
                onReactionAdd={onReactionAdd}
                onReactionRemove={onReactionRemove}
                conversationColor={conversationColor}
                sx={{ minWidth: '120px', maxWidth: '100%' }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.4,
                    fontSize: '16px',
                    '& a': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                      textDecorationColor: 'primary.main',
                      textUnderlineOffset: '2px',
                      transition: 'all 0.2s ease',
                      '&:hover': { color: 'primary.dark' },
                      '&:visited': { color: 'primary.main' }
                    },
                    '& .mention-tag': { color: '#1976d2', fontWeight: 600 },
                    ...sxMsgText
                  }}
                >
                  {formatMessageContent(msg.message, participants, isUserMessage)}
                   
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                 <MessageTimestamp />
                </Box>
                </Typography>
                
              </BaseBubble>
            </Box>
          </Box>
          {(msg.reaction_counts || msg.total_reactions > 0) && (
            <Box sx={{ mt: 0.5, ml: 6, display: 'flex', justifyContent: 'flex-start' }}>
              <MessageReactions
                message={msg}
                isUserMessage={isUserMessage}
                reactions={msg.reaction_counts || {}}
                totalReactions={msg.total_reactions || 0}
                userReactions={Array.isArray(msg.user_reactions) ? msg.user_reactions : []}
                onReactionAdd={onReactionAdd}
                onReactionRemove={onReactionRemove}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
